import { useBandRegionalSettings } from "Bands/utils";
import React, { useCallback, useMemo } from "react";
import { copy } from "shared/assets";
import { File } from "shared/assets/icons";

import { FancyAnchorLink, MainTeaser, RTEContent, TeaserProps } from "shared/components";
import { Spacer } from "shared/layout";
import { BoldText, CodeText, FlexContainer, Icon, MutedText, SmallText } from "shared/styles";
import { colors } from "shared/styles/constants";
import { IFile } from "shared/types";
import { getCountry, isoToDateString } from "shared/utils";

const documentCopy = copy.document;

interface DocumentTeaserProps extends TeaserProps {
  title?: string;
  passportNumber?: string;
  visaNumber?: string;
  formNumber?: string;
  permitNumber?: string;
  vehicleCategories?: string[];
  otherVehicleCategories?: string[];
  licensePlate?: string;
  brand?: string;
  model?: string;
  color?: string;
  membershipNumber?: string;
  membershipStatus?: string;
  issuingCountry?: string;
  issuingAuthority?: string;
  issueDate?: string;
  expiryDate?: string;
  info?: string;
  files?: IFile[];
  subTitle?: string;
}

export const DocumentTeaser: React.FC<DocumentTeaserProps> = ({
  title,
  passportNumber,
  visaNumber,
  formNumber,
  permitNumber,
  vehicleCategories = [],
  otherVehicleCategories = [],
  licensePlate,
  brand,
  model,
  color,
  membershipNumber,
  membershipStatus,
  issuingCountry,
  issuingAuthority,
  issueDate,
  expiryDate,
  info,
  files,
  ...rest
}) => {
  const { dateFormat } = useBandRegionalSettings();
  const mappedCountry = issuingCountry ? getCountry(issuingCountry) : null;

  const renderLabel = useCallback(
    (label: string) => <SmallText as={MutedText}>{label}:</SmallText>,
    []
  );

  const renderValue = useCallback(
    (value: string) => (
      <SmallText as={MutedText}>
        <BoldText>{value}</BoldText>
      </SmallText>
    ),
    []
  );

  const renderNumber = useCallback(
    (number?: string) => (
      <div>
        <CodeText>{number}</CodeText>
      </div>
    ),
    []
  );

  const renderLine = useCallback(
    ({
      label,
      value,
      numbers,
    }: {
      label: string;
      value?: JSX.Element | string;
      numbers?: string[];
    }) => {
      if (!value && !numbers?.length) return;

      return (
        <FlexContainer>
          {renderLabel(label)}
          {typeof value === "string" && renderValue(value)}
          {numbers?.map(number => (
            <React.Fragment key={number}>{renderNumber(number)}</React.Fragment>
          ))}
        </FlexContainer>
      );
    },
    [renderLabel, renderNumber, renderValue]
  );

  const visaPassportNumber = useMemo(() => {
    if (!visaNumber && !passportNumber) return null;

    return (
      <Spacer size="xs" spaceDirection="horizontal">
        {visaNumber &&
          renderLine({
            label: documentCopy.visaNumber,
            numbers: [visaNumber],
          })}

        {passportNumber &&
          renderLine({
            label: documentCopy.passportNumber,
            numbers: [passportNumber],
          })}
      </Spacer>
    );
  }, [passportNumber, renderLine, visaNumber]);

  const issuedBy = useMemo(() => {
    if (!mappedCountry && !issuingAuthority) return null;

    return (
      <Spacer size="xs" spaceDirection="horizontal">
        {renderLabel(documentCopy.issuedBy)}
        {mappedCountry && (
          <>
            {mappedCountry.flag}
            {renderValue(mappedCountry.name)}
          </>
        )}
        {issuingAuthority && renderValue(issuingAuthority)}
      </Spacer>
    );
  }, [issuingAuthority, mappedCountry, renderLabel, renderValue]);

  const issueExpiryDate = useMemo(() => {
    if (!issueDate && !expiryDate) return null;

    return (
      <Spacer size="xs" spaceDirection="horizontal">
        {renderLine({
          label: documentCopy.issuedOn,
          value: issueDate && isoToDateString(issueDate, dateFormat),
        })}

        {renderLine({
          label: documentCopy.expiresOn,
          value: expiryDate && isoToDateString(expiryDate, dateFormat),
        })}
      </Spacer>
    );
  }, [dateFormat, expiryDate, issueDate, renderLine]);

  const vehicleInfo = useMemo(() => {
    if (!brand && !model && !color) return null;

    return (
      <Spacer size="xs" spaceDirection="horizontal">
        {renderLine({
          label: documentCopy.brand,
          value: brand,
        })}

        {renderLine({
          label: documentCopy.model,
          value: model,
        })}

        {renderLine({
          label: documentCopy.color,
          value: color,
        })}
      </Spacer>
    );
  }, [brand, color, model, renderLine]);

  const membershipInfo = useMemo(() => {
    if (!membershipNumber && !membershipStatus) return null;

    return (
      <Spacer size="xs" spaceDirection="horizontal">
        {membershipNumber &&
          renderLine({
            label: documentCopy.brand,
            numbers: [membershipNumber],
          })}

        {membershipStatus &&
          renderLine({
            label: documentCopy.model,
            numbers: [membershipStatus],
          })}
      </Spacer>
    );
  }, [membershipNumber, membershipStatus, renderLine]);

  const fileLinks = useMemo(() => {
    if (!files?.length) return null;

    return (
      <div>
        <SmallText as={MutedText}>Files:</SmallText>
        <FlexContainer style={{ marginTop: "2px" }}>
          <Icon as={File} color={colors.grey4} />
          {files.map(({ id, title, source = "" }, index) => (
            <FancyAnchorLink key={id} href={source} target="_blank">
              <SmallText>{title || `File ${index + 1}`}</SmallText>
            </FancyAnchorLink>
          ))}
        </FlexContainer>
      </div>
    );
  }, [files]);

  const subTitleComponent = useMemo(
    () => (
      <Spacer size="s">
        {visaPassportNumber}

        {formNumber &&
          renderLine({
            label: documentCopy.formNumber,
            numbers: [formNumber],
          })}

        {permitNumber &&
          renderLine({
            label: documentCopy.permitNumber,
            numbers: [permitNumber],
          })}

        <div style={{ marginLeft: "-4px" }}>
          {renderLine({
            label: documentCopy.categories,
            numbers: [
              ...vehicleCategories.map(cat => cat.replaceAll("_", " + ")),
              ...otherVehicleCategories,
            ],
          })}
        </div>

        {licensePlate &&
          renderLine({
            label: documentCopy.licensePlate,
            numbers: [licensePlate],
          })}

        {vehicleInfo}

        {issuedBy}

        {membershipInfo}

        {issueExpiryDate}

        {fileLinks}

        {info && (
          <>
            <SmallText as={MutedText}>Additional info:</SmallText>
            <RTEContent content={info} />
          </>
        )}
      </Spacer>
    ),
    [
      fileLinks,
      formNumber,
      info,
      issueExpiryDate,
      issuedBy,
      licensePlate,
      membershipInfo,
      otherVehicleCategories,
      permitNumber,
      renderLine,
      vehicleCategories,
      vehicleInfo,
      visaPassportNumber,
    ]
  );
  return (
    <div>
      <MainTeaser
        title={title}
        subTitleComponent={subTitleComponent}
        data-testid="document_teaser"
        {...rest}
      />
    </div>
  );
};
